import React, { useState, useEffect } from "react"
import {
  Breadcrumb,
  notification,
  Spin,
  Card,
  Row,
  Col,
  Typography,
  Space,
  Tag,
  Tabs,
} from "antd"
import MetaTags from "react-meta-tags"
import { ProjectPackages } from "./ProjectPackages"
import ProjectPackageList from "pages/ProjectPackage"
import { resetGasProjectFlag } from "store/actions"
import { getGasProjectsFullFilterApi } from "helpers/backend_helper"
import { connect } from "react-redux"
import { withTranslation } from "react-i18next"
import { useParams, Link } from "react-router-dom"
import {
  HomeOutlined,
  UserOutlined,
  EnvironmentOutlined,
  ToolOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons"

const { Title, Text } = Typography

const GasProjectDashboardProcess = props => {
  const { loading, onResetGasProjectFlag, triggerReload } = props
  const [api, contextHolder] = notification.useNotification()
  const [errorMessage, setErrorMessage] = useState(null)
  const [gasProject, setGasProject] = useState(null)
  const [projectLoading, setProjectLoading] = useState(false)
  const { id } = useParams()

  const openNotification = (type, placement, message) => {
    api[type]({
      message,
      placement,
    })
  }

  const firstLoad = async () => {
    if (!id) return

    setProjectLoading(true)
    try {
      const searchQuery = {
        id,
        page: 1,
        pageSize: 1,
        fileNumber: 0,
        projectLevelEnum: 100,
        isStop: false,
      }
      const response = await getGasProjectsFullFilterApi(searchQuery)
      if (response?.data?.length > 0) {
        setGasProject(response.data[0])
      }
    } catch (error) {
      openNotification("error", "top", "خطا در دریافت اطلاعات پرونده")
    } finally {
      setProjectLoading(false)
    }
  }

  useEffect(() => {
    if (props.success) {
      openNotification("success", "top", props.success)
    }
    if (props.error) {
      openNotification("error", "top", props.error)
    }
    if (errorMessage) {
      openNotification("warning", "top", errorMessage)
    }
    onResetGasProjectFlag()
  }, [errorMessage, props.success, props.error])

  // Breadcrumb configuration
  const breadcrumbItems = [
    { title: <Link to="/dashboard">داشبورد</Link> },
    { title: <Link to="/projects/GasProjectProcess">تخصیص</Link> },
    {
      title: gasProject ? (
        <Space>
          <Text strong style={{ fontFamily: "tahoma" }}>
            {gasProject.fileNumber}
          </Text>
          <Text type="secondary">|</Text>
          <Text>{gasProject.landlordName}</Text>
        </Space>
      ) : (
        "در حال بارگذاری..."
      ),
    },
  ]

  // Project Info Card
  const ProjectInfoCard = () => {
    if (!gasProject) return null

    return (
      <Card
        size="small"
        style={{
          marginBottom: 16,
          background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)",
          borderRadius: 12,
        }}
      >
        <Row gutter={[16, 12]}>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" size={2}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                <UserOutlined style={{ marginLeft: 4 }} />
                مالک
              </Text>
              <Text strong>{gasProject.landlordName || "-"}</Text>
            </Space>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Space direction="vertical" size={2}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                <HomeOutlined style={{ marginLeft: 4 }} />
                طبقه/واحد
              </Text>
              <Text strong>
                {gasProject.floorNumber === 0
                  ? gasProject.unitNumber > 1
                    ? "چند واحدی"
                    : "همکف"
                  : `طبقه ${gasProject.floorNumber}`}{" "}
                / {gasProject.unitNumber} واحد
              </Text>
            </Space>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Space direction="vertical" size={2}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                وضعیت
              </Text>
              <Tag color={gasProject.isCancel ? "error" : "success"}>
                {gasProject.projectLevel}
              </Tag>
            </Space>
          </Col>
          <Col xs={24} sm={12} md={10}>
            <Space direction="vertical" size={2}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                <EnvironmentOutlined style={{ marginLeft: 4 }} />
                آدرس
              </Text>
              <Text style={{ fontSize: 12 }}>{gasProject.address || "-"}</Text>
            </Space>
          </Col>
        </Row>
      </Card>
    )
  }

  useEffect(() => {
    firstLoad()
  }, [id, triggerReload])

  return (
    <div className="page-content">
      <MetaTags>
        <title>پکیج‌ها و نصاب | واحد گاز</title>
      </MetaTags>

      {/* Breadcrumb */}
      <Breadcrumb
        separator=">"
        style={{ margin: "16px 0", fontSize: 14 }}
        items={breadcrumbItems}
      />

      {contextHolder}

      {/* Loading Spinner */}
      <Spin
        spinning={projectLoading && !gasProject}
        tip="در حال بارگذاری..."
        size="large"
      >
        <div style={{ minHeight: 200 }}>
          {!projectLoading && !gasProject ? (
            <Card>
              <div style={{ textAlign: "center", padding: 40 }}>
                اطلاعات پرونده یافت نشد.
              </div>
            </Card>
          ) : (
            gasProject && (
              <>
                {/* Project Info Header */}
                <ProjectInfoCard />

                {/* Tabs for Management and List */}
                <Card
                  bodyStyle={{ padding: 0 }}
                  style={{ borderRadius: 12 }}
                >
                  <Tabs
                    defaultActiveKey="management"
                    type="card"
                    style={{ margin: 0 }}
                    items={[
                      {
                        key: "management",
                        label: (
                          <Space>
                            <ToolOutlined />
                            <span>مدیریت پکیج‌ها و نصاب</span>
                          </Space>
                        ),
                        children: (
                          <div style={{ padding: 16 }}>
                            <ProjectPackages
                              rowData={gasProject}
                              openNotification={openNotification}
                              mainProps={props}
                            />
                          </div>
                        ),
                      },
                      {
                        key: "list",
                        label: (
                          <Space>
                            <UnorderedListOutlined />
                            <span>لیست پکیج‌ها</span>
                          </Space>
                        ),
                        children: (
                          <ProjectPackageList
                            gasProjectId={id}
                            disableEdit={true}
                            embedded={true}
                          />
                        ),
                      },
                    ]}
                  />
                </Card>
              </>
            )
          )}
        </div>
      </Spin>
    </div>
  )
}

const mapStateToProps = ({ GasProjects }) => ({
  error: GasProjects.error,
  success: GasProjects.success,
  loading: GasProjects.loading,
  triggerReload: GasProjects.triggerReload,
})

const mapDispatchToProps = dispatch => ({
  onResetGasProjectFlag: () => dispatch(resetGasProjectFlag()),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(GasProjectDashboardProcess))
